/**
 * Derivative reconciliation orchestration.
 *
 * Mirrors the recon/run and recon/cash endpoints in recon-dashboard
 * (api/function_app.py + api/recon_parsing.py), which are the source of truth.
 * Parsing and reconciliation run in the browser, so the returned payload has
 * the same shape those endpoints produce and the views can stay identical.
 */
import * as XLSX from 'xlsx';
import { toDateObj } from '../utils/derivativeReconFormat';
import {
  buildMillimanCashFlows,
  buildWiresIndex,
  parseCustodyFile,
  reconcileCash,
  reconcilePositions,
  reconcileTx,
  serializeCashResults,
} from '../utils/derivativeReconLogic';

/** Milliman sheets we consume beyond the first; the rest are summary tabs. */
const MIL_SHEETS = ['Index Option Transactions', 'Payoffs_all'];

/** CW payoffs land under either transaction type, so both pool per trade. */
const PAYOFF_TX_TYPES = ['Option Settle', 'Expiration'];

const HEADER_SCAN_ROWS = 25;

// ── Sheet parsing ────────────────────────────────────────────────────────────

const cellText = (c) => (c == null ? '' : String(c).trim());

/**
 * Locate the header row in a raw (header-less) sheet.
 *
 * Prefers a row containing an "Identifier" or "Trade ID" cell; otherwise falls
 * back to the densest row within the first 25. Vendor exports routinely carry
 * several title/metadata rows above the real header.
 */
export function findHeaderRow(raw) {
  const n = Math.min(HEADER_SCAN_ROWS, raw.length);
  for (let i = 0; i < n; i += 1) {
    const cells = (raw[i] || []).map((c) => cellText(c).toLowerCase());
    if (cells.includes('identifier') || cells.includes('trade id')) return i;
  }
  let best = 0;
  let bestN = 0;
  for (let j = 0; j < n; j += 1) {
    const nonEmpty = (raw[j] || []).filter((c) => cellText(c) !== '').length;
    if (nonEmpty > bestN) {
      bestN = nonEmpty;
      best = j;
    }
  }
  return best;
}

/**
 * Turn a header row into unique keys. Blank cells become `col_<i>`; repeats get
 * a numeric suffix so duplicate labels can't shadow one another.
 */
export function dedupeHeaders(headerRow) {
  const seen = new Map();
  return (headerRow || []).map((h, i) => {
    const base = cellText(h) === '' ? `col_${i}` : cellText(h);
    const n = (seen.get(base) || 0) + 1;
    seen.set(base, n);
    return n === 1 ? base : `${base}_${n}`;
  });
}

/**
 * Rows of a worksheet, keyed by its real header row.
 *
 * Rows that are empty across every mapped column are dropped: a sheet styled
 * over a large range reports thousands of blank rows after its data, and
 * carrying them through fills the grids with phantom entries and the recon
 * with rows that match nothing. A zero is a value, not a blank.
 */
export function sheetToRows(sheet) {
  if (!sheet) return [];
  const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, blankrows: true });
  if (!raw.length) return [];
  const headerIdx = findHeaderRow(raw);
  const headers = dedupeHeaders(raw[headerIdx]);
  const rows = [];
  for (let i = headerIdx + 1; i < raw.length; i += 1) {
    const src = raw[i] || [];
    const row = {};
    let empty = true;
    headers.forEach((h, j) => {
      const v = j < src.length ? src[j] : null;
      row[h] = v === undefined || v === '' ? null : v;
      if (row[h] !== null) empty = false;
    });
    if (!empty) rows.push(row);
  }
  return rows;
}

/**
 * Read a File into `{ sheetName: rows }`.
 *
 * `sheetsWanted` limits parsing to the first sheet plus the named ones
 * (case-insensitive); pass null to keep every sheet. SheetJS sniffs xlsx/xls/
 * csv/html, which covers the vendor exports that carry a misleading extension
 * (Northern Trust ships Cash_Activity_Detail.xls as HTML).
 */
async function readWorkbook(file, sheetsWanted = null) {
  const ab = await file.arrayBuffer();
  const wb = XLSX.read(ab, { type: 'array', cellDates: true });
  const available = wb.SheetNames;
  let toRead = available;
  if (sheetsWanted) {
    const wanted = new Set(sheetsWanted.map((s) => s.trim().toLowerCase()));
    toRead = available.filter(
      (sn, i) => i === 0 || wanted.has(String(sn).trim().toLowerCase()),
    );
  }
  const out = {};
  for (const sn of toRead) out[sn] = sheetToRows(wb.Sheets[sn]);
  return out;
}

/** Pick a sheet by case-insensitive name, or the first sheet when unnamed. */
export function parseSheet(sheets, sheetName = null) {
  const names = Object.keys(sheets || {});
  if (sheetName) {
    const target = sheetName.trim().toLowerCase();
    const hit = names.find((n) => String(n).trim().toLowerCase() === target);
    return hit ? sheets[hit] : [];
  }
  return names.length ? sheets[names[0]] : [];
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function runDerivativeRecon({ mil1, mil2, cwPos, cwTx, ntFile, jpmFile }) {
  const [wb1, wb2, cwPosWb, cwTxWb] = await Promise.all([
    readWorkbook(mil1, MIL_SHEETS),
    readWorkbook(mil2, MIL_SHEETS),
    readWorkbook(cwPos),
    readWorkbook(cwTx),
  ]);

  const milPosRows = [...parseSheet(wb1), ...parseSheet(wb2)];
  const cwPosRows = parseSheet(cwPosWb);
  const cwTxRows = parseSheet(cwTxWb);
  const milPremRows = [
    ...parseSheet(wb1, 'Index Option Transactions'),
    ...parseSheet(wb2, 'Index Option Transactions'),
  ];
  const milPayRows = [
    ...parseSheet(wb1, 'Payoffs_all'),
    ...parseSheet(wb2, 'Payoffs_all'),
  ];

  const posResults = reconcilePositions(milPosRows, cwPosRows);
  const premResults = milPremRows.length
    ? reconcileTx(milPremRows, cwTxRows, 'Total Premium', 'Settlement Date', 'Buy')
    : [];
  const payResults = reconcileTx(
    milPayRows, cwTxRows, 'Payoff', 'Payoff Date', PAYOFF_TX_TYPES,
  );

  const milFlows = buildMillimanCashFlows(milPremRows, milPayRows);

  let cashResults = null;
  let wiresIdx = { wires: {}, tradeWireIndex: {} };
  if (ntFile || jpmFile) {
    const custody = [];
    if (ntFile) custody.push(...parseCustodyFile(parseSheet(await readWorkbook(ntFile)), 'NT'));
    if (jpmFile) custody.push(...parseCustodyFile(parseSheet(await readWorkbook(jpmFile)), 'JPM'));
    cashResults = serializeCashResults(reconcileCash(milFlows, custody));
    wiresIdx = buildWiresIndex(cashResults);
  }

  return {
    posResults,
    premResults,
    payResults,
    milRaw: milPosRows,
    milPremRaw: milPremRows,
    milPayRaw: milPayRows,
    cwRaw: cwPosRows,
    cwTxRaw: cwTxRows,
    milFlows: milFlows.map(({ date, ...f }) => f),
    cashResults,
    wires: wiresIdx.wires,
    tradeWireIndex: wiresIdx.tradeWireIndex,
  };
}

export async function runDerivativeCashRec({ flows, ntFile, jpmFile }) {
  // Flows arrive with their Date stripped for storage; rebuild it from dateStr.
  const flowsWithDates = (flows || [])
    .map((f) => ({ ...f, date: f.date instanceof Date ? f.date : toDateObj(f.dateStr) }))
    .filter((f) => f.date);

  const custody = [];
  if (ntFile) custody.push(...parseCustodyFile(parseSheet(await readWorkbook(ntFile)), 'NT'));
  if (jpmFile) custody.push(...parseCustodyFile(parseSheet(await readWorkbook(jpmFile)), 'JPM'));

  const cashResults = serializeCashResults(reconcileCash(flowsWithDates, custody));
  const { wires, tradeWireIndex } = buildWiresIndex(cashResults);
  return { cashResults, wires, tradeWireIndex };
}

export { downloadDerivativeReconExport } from '../utils/derivativeReconExport';
